import { Component, OnInit } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { PatientService } from './services/patient.service';
import { Patient } from './services/patient.model';
import { SharedService } from 'src/app/services/shared.service';
import { CaseType, MaxLength, Page, ToastType } from 'src/app/enums/enums';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { Attorney, GetItemsParams, GetPatientParams } from '../attorney/service/attorney.model';
import { AttorneyService } from '../attorney/service/attorney.service';
import { Clinic } from '../clinic/services/clinic.model';
import { ApiService } from 'src/app/services/api.service';
import { MakeAppointmentDialogueComponent } from 'src/app/components/make-appointment-dialogue/make-appointment-dialogue.component';
import { DialogueService } from 'src/app/services/dialogue.service';
import { AddEditPatientDialogueComponent } from 'src/app/components/add-edit-patient-dialogue/add-edit-patient-dialogue.component';

@Component({
  selector: 'app-patients',
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.scss']
})
export class PatientsComponent implements OnInit {
  public IsLoading = false;
  public ShowDischargedPatients = false;
  public IsEditPatient = false;
  patientsList: Patient[] = [];
  public TotalPatients = 0;
  PatientForm: FormGroup;
  attorneyList: Attorney[] = [];
  attorneyListGlobal: Attorney[] = [];
  currentClinic: number = 0;
  private Unsubscribe = new Subject<void>();
  private SearchSubject = new Subject<string>();
  public Clinincs: Clinic[] = [];
  public SearchVal: string = '';
  public PaginationParams!: GetPatientParams;
  public MaxZipLength = MaxLength.Zip;
  public caseTypeOptions: { key: number; value: string }[] = [];

  constructor(
    private patientService: PatientService,
    public sharedService: SharedService,
    private toast: ToastService,
    private attorneyService: AttorneyService,
    public apiService: ApiService,
    private dialogue: DialogueService
  ) {
    this.PaginationParams = {
      searchValue: '',
      skip: 0,
      take: Number(this.sharedService.getData('records')) || Page.Size,
      sortColumn: 'lastName',
      clinicId: this.sharedService.CurrentClinic.value || 0,
      sortOrder: 'asc',
      isDischarged: false
    };
    this.PatientForm = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required]),
      dateOfBirth: new FormControl(''),
      dateOfAccident: new FormControl('', [Validators.required]),
      dateOfEntry: new FormControl('', [Validators.required]),
      caseType: new FormControl('', [Validators.required]),
      attorneyId: new FormControl('', [Validators.required]),
      disabled: new FormControl(false),
      transportation: new FormControl(false), // Optional field
      oV_W: new FormControl('', [Validators.required]),
      non_Compliant: new FormControl(true),
      clinicId: new FormControl('', [Validators.required]),
      note: new FormControl(''),
      address: new FormControl(''),
      town: new FormControl(''),
      zip: new FormControl('', [Validators.pattern('^[0-9]{5}$')])
    });
    this.apiService.ClinicList.subscribe((list) => (this.Clinincs = list));
    this.attorneyService.getAttorneyByClinic(0).subscribe((result) => (this.attorneyListGlobal = result.result));
    this.caseTypeOptions = Object.keys(CaseType)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        key: Number(key),
        value: CaseType[Number(key)]
      }));
  }

  ngOnInit(): void {
    this.SearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        this.PaginationParams.searchValue = term;
        this.GetPatientsList(this.PaginationParams);
        return '';
      })
    ).subscribe((results) => {});
    this.sharedService.CurrentClinic.pipe(takeUntil(this.Unsubscribe)).subscribe((val) => {
      this.PaginationParams.clinicId = Number(val);
      this.GetPatientsList(this.PaginationParams);
    });
    this.PatientForm.get('transportation')?.valueChanges.subscribe((isChecked) => {
      this.onTransportationChange(isChecked);
    });
  }

  GetPatientsList(params: GetPatientParams) {
    this.IsLoading = true;
    this.patientService.GetPatients(params).subscribe({
      next: (res) => {
        this.patientsList = [...res.result.data];
        this.patientsList.map((patient: Patient) => {
          patient.caseName = this.getCaseName(patient.caseType);
          patient.attorneyName = this.getAttorneyName(patient.attorneyId);
          return patient;
        });
        this.TotalPatients = res.result.count;
      },
      error: (error) => {
        this.toast.showToast({
          message: error.message,
          type: ToastType.Error
        });
      },
      complete: () => (this.IsLoading = false)
    });
  }

  GetDischargedPatientsList(value: boolean) {
    this.ShowDischargedPatients = value;
    this.PaginationParams.isDischarged = value;
    this.GetPatientsList(this.PaginationParams);
  }

  onTransportationChange(isChecked: boolean) {
    const address = this.PatientForm.get('address');
    const town = this.PatientForm.get('town');
    const zip = this.PatientForm.get('zip');

    if (isChecked) {
      address?.setValidators([Validators.required]);
      town?.setValidators([Validators.required]);
      zip?.setValidators([Validators.required, Validators.pattern('^[0-9]{5}$')]);
    } else {
      address?.clearValidators();
      town?.clearValidators();
      zip?.setValidators([Validators.pattern('^[0-9]{5}$')]);
    }
    address?.updateValueAndValidity();
    town?.updateValueAndValidity();
    zip?.updateValueAndValidity();
  }

  getAttorneyName(id: number): string {
    let attorney = this.attorneyListGlobal.find((attorney) => {
      return attorney.id === id;
    });
    return attorney?.attorneyName ? attorney.attorneyName : '';
  }

  getCaseName(id: number): string {
    return CaseType[id] || '';
  }

  onPageChange(newPage: number): void {
    this.PaginationParams.skip = (newPage - 1) * this.PaginationParams.take;
    this.GetPatientsList(this.PaginationParams);
  }

  onPageSizeChange(newPageSize: number): void {
    this.PaginationParams.take = newPageSize;
    this.PaginationParams.skip = 0;
    this.sharedService.setData('records', this.PaginationParams.take);
    this.GetPatientsList(this.PaginationParams);
  }

  OpenAddPopup() {
    const data = {
      isEditPatient: false
    };

    const dialogueRef = this.dialogue.Open(AddEditPatientDialogueComponent, { size: 'xl' }, data);
    dialogueRef.result.then((data) => {
      if (data) {
        this.GetPatientsList(this.PaginationParams);
      }
    });
  }

  OpenEditPopup(id: number) {
    const data = {
      isEditPatient: true,
      PatinentId: id
    };
    const dialogueRef = this.dialogue.Open(AddEditPatientDialogueComponent, { size: 'xl' }, data);
    dialogueRef.result.then((data) => {
      if (data) {
        this.GetPatientsList(this.PaginationParams);
      }
    });
  }

  OpenApptDialogue(patient: Patient) {
    let data = {
      name: patient.firstName + patient.lastName,
      caseName: patient.caseName,
      da: patient.dateOfAccident,
      de: patient.dateOfEntry,
      patientId: patient.id,
      clinicId: patient.clinicId
    };
    this.dialogue.Open(MakeAppointmentDialogueComponent, { size: 'xl' }, data);
  }

  SearchData() {
    this.SearchSubject.next(this.SearchVal);
  }

  SortData(sortBy: string) {
    this.PaginationParams.sortColumn = sortBy;
    this.PaginationParams.sortOrder = this.PaginationParams.sortOrder === 'asc' ? 'desc' : 'asc';
    this.GetPatientsList(this.PaginationParams);
  }

  ngOnDestroy(): void {
    this.Unsubscribe.next();
    this.Unsubscribe.complete();
  }
}
